import { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BadgeTier = 'friend' | 'family' | 'fam' | 'legend';

interface LeaderboardEntry {
  rank: number;
  name: string;
  badgeTier: BadgeTier;
  totalInvested: number;
  campaignsCount: number;
  isCurrentUser?: boolean;
}

interface PowerFam {
  rank: number;
  creatorName: string;
  campaignTitle: string;
  avgContribution: number;
  supporterCount: number;
  totalRaised: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TIER_COLORS: Record<BadgeTier, string> = {
  friend:  '#f43f5e',
  family:  '#f97316',
  fam:     '#6366f1',
  legend:  '#eab308',
};

const RANK_MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

function fmt(amount: number) {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// ---------------------------------------------------------------------------
// Fallback seed data (shown when DB returns no results)
// ---------------------------------------------------------------------------

const SEED_INVESTORS: LeaderboardEntry[] = [
  { rank: 1, name: 'Priya P.',  badgeTier: 'legend', totalInvested: 187500, campaignsCount: 12 },
  { rank: 2, name: 'James W.',  badgeTier: 'legend', totalInvested: 94200,  campaignsCount: 8 },
  { rank: 3, name: 'Morgan L.', badgeTier: 'fam',    totalInvested: 72000,  campaignsCount: 7 },
  { rank: 4, name: 'Aisha K.',  badgeTier: 'fam',    totalInvested: 41800,  campaignsCount: 5 },
  { rank: 5, name: 'Derek T.',  badgeTier: 'family', totalInvested: 28500,  campaignsCount: 4 },
];

const SEED_POWER_FAMS: PowerFam[] = [
  { rank: 1, creatorName: 'Jordan', campaignTitle: 'Getting My First Family Car', avgContribution: 1850, supporterCount: 4, totalRaised: 7400 },
  { rank: 2, creatorName: 'Maya',   campaignTitle: 'Tesla Model 3',               avgContribution: 1250, supporterCount: 6, totalRaised: 7500 },
  { rank: 3, creatorName: 'Darius', campaignTitle: 'First Car for My Kids',       avgContribution: 875,  supporterCount: 8, totalRaised: 7000 },
];

// ---------------------------------------------------------------------------
// IndividualBoard
// ---------------------------------------------------------------------------

function IndividualBoard({ entries }: { entries: LeaderboardEntry[] }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {entries.map((entry) => (
        <div
          key={entry.rank}
          className={`flex items-center gap-3 px-5 py-3.5 border-b border-gray-50 last:border-0 transition-colors ${
            entry.isCurrentUser
              ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200'
              : 'hover:bg-gray-50'
          }`}
        >
          <span className="w-8 text-center text-sm font-bold flex-shrink-0">
            {RANK_MEDAL[entry.rank] ?? <span className="text-gray-300">{entry.rank}</span>}
          </span>
          <div
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: TIER_COLORS[entry.badgeTier] }}
          />
          <span className={`flex-1 text-sm font-semibold ${entry.isCurrentUser ? 'text-indigo-700' : 'text-gray-800'}`}>
            {entry.name}
            {entry.isCurrentUser && <span className="ml-1.5 text-xs font-normal text-indigo-400">(you)</span>}
          </span>
          <span
            className="text-xs font-semibold capitalize px-2 py-0.5 rounded-full flex-shrink-0"
            style={{ color: TIER_COLORS[entry.badgeTier], backgroundColor: `${TIER_COLORS[entry.badgeTier]}22` }}
          >
            {entry.badgeTier}
          </span>
          <span className="text-xs font-semibold text-gray-600 flex-shrink-0 tabular-nums">{fmt(entry.totalInvested)}</span>
          <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
            {entry.campaignsCount} {entry.campaignsCount === 1 ? 'campaign' : 'campaigns'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PowerFamBoard
// ---------------------------------------------------------------------------

function PowerFamBoard({ fams }: { fams: PowerFam[] }) {
  return (
    <div className="space-y-3">
      {fams.map((fam) => (
        <div
          key={fam.rank}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-start gap-4 hover:shadow-md transition-shadow"
        >
          <div className="flex flex-col items-center gap-1 flex-shrink-0 w-10">
            <span className="text-xl leading-none">
              {RANK_MEDAL[fam.rank] ?? <span className="text-sm font-bold text-gray-400">#{fam.rank}</span>}
            </span>
            {fam.rank === 1 && (
              <span className="text-xs font-bold text-amber-600 bg-amber-100 rounded-full px-1.5 py-0.5 whitespace-nowrap">Power FAM</span>
            )}
          </div>
          <div className="text-3xl leading-none flex-shrink-0 mt-0.5">🚗</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-bold text-gray-900 text-base">{fam.creatorName}</span>
              <span className="text-sm text-gray-500 truncate">{fam.campaignTitle}</span>
            </div>
            <p className="text-indigo-600 font-bold text-xl mt-1 tabular-nums">{fmt(fam.avgContribution)} avg</p>
            <p className="text-gray-400 text-xs mt-0.5">{fam.supporterCount} supporters from personal network</p>
          </div>
          <div className="flex-shrink-0 text-right">
            <span className="inline-block bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full tabular-nums">
              {fmt(fam.totalRaised)} raised
            </span>
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-400 text-center pt-1">
        Power FAMs: campaigns where friends &amp; family averaged $300+ per contributor in days 1–3.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard (exported)
// ---------------------------------------------------------------------------

export function Leaderboard() {
  const [activeList, setActiveList] = useState<'individuals' | 'powerfams'>('individuals');
  const [investors, setInvestors] = useState<LeaderboardEntry[]>([]);
  const [powerFams, setPowerFams] = useState<PowerFam[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/leaderboard')
      .then((r) => r.json())
      .then((data: { investors?: Array<Record<string, unknown>>; powerFams?: Array<Record<string, unknown>> }) => {
        const VALID_TIERS = new Set<BadgeTier>(['friend', 'family', 'fam', 'legend']);
        const inv: LeaderboardEntry[] = (data.investors ?? []).map((row, i) => ({
          rank: i + 1,
          name: row.name ? String(row.name).split(' ')[0] + ' ' + (String(row.name).split(' ')[1]?.[0] ?? '') + '.' : 'Anonymous',
          badgeTier: VALID_TIERS.has(row.badge_tier as BadgeTier) ? (row.badge_tier as BadgeTier) : 'friend',
          totalInvested: Number(row.total_contributed ?? 0),
          campaignsCount: Number(row.contribution_count ?? 0),
        }));
        const pf: PowerFam[] = (data.powerFams ?? []).map((row, i) => ({
          rank: i + 1,
          creatorName: row.creator_name ? String(row.creator_name).split(' ')[0] : 'Creator',
          campaignTitle: String(row.title ?? ''),
          avgContribution: Number(row.avg_contribution ?? 0),
          supporterCount: Number(row.supporter_count ?? 0),
          totalRaised: Number(row.total_raised ?? 0),
        }));
        setInvestors(inv.length > 0 ? inv : SEED_INVESTORS);
        setPowerFams(pf.length > 0 ? pf : SEED_POWER_FAMS);
      })
      .catch(() => {
        setInvestors(SEED_INVESTORS);
        setPowerFams(SEED_POWER_FAMS);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Trophy className="w-5 h-5 text-amber-500" />
        <h2 className="text-xl font-bold text-gray-900">FAM Leaderboard</h2>
      </div>

      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveList('individuals')}
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
            activeList === 'individuals' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Individual Investors
        </button>
        <button
          onClick={() => setActiveList('powerfams')}
          className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
            activeList === 'powerfams' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ⚡ Power FAMs
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activeList === 'individuals' ? (
        <IndividualBoard entries={investors} />
      ) : (
        <PowerFamBoard fams={powerFams} />
      )}
    </div>
  );
}
